import type { ReactNode } from "react";
import {
  ArrowDown,
  ArrowUp,
  ChevronsDownUp,
  ChevronsUpDown,
  ClipboardPaste,
  Copy,
  Scissors,
  type LucideIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useZamokTabulky } from "@/components/zakazka/zamokTabulky";

/**
 * Menu riadku tabuľky dielcov (KOLO 181, návrh A2) — ⋮ na konci riadku
 * alebo pravé tlačidlo kdekoľvek na riadku.
 *
 * User si vybral A2 zo stránky návrhov: stĺpec ikon (160 px, ikony len
 * pri myši) robil „vedľa materiálu také voľno". Hore je **rad ikon** ako
 * v kontextovom menu Windows 11 — *„tam daj hore ikonky zbaliť/rozbaliť
 * a kopírovať, vložiť a vystrihnúť"* — pod ním položky so skratkou.
 *
 * KOLO 184 (user: *„nech to menu nie je také veľké"*, vybral „Ikony
 * hore, krátky zoznam"): v rade ikon pribudlo Posunúť hore / dole, menu
 * je užšie (208 px) a položky nižšie — pridali sa Pridať dielec, Pridať
 * kovanie a Zbaliť všetko, a aj tak je menšie ako predtým (~240 × 340).
 *
 * Ukotvenie je neviditeľný bod s `position: fixed` — pri pravom tlačidle
 * na súradniciach kurzora, pri ⋮ v rohu tlačidla. Menu sa montuje len
 * otvorené (stav drží riadok), takže 200 riadkov nenesie 200 inštancií.
 */

export interface IkonaMenu {
  onClick: () => void;
  /** Nedostupná ikona je sivá, `dovod` je v `title`. */
  dovod?: string | null;
}

export interface PolohaMenu {
  x: number;
  y: number;
  /** ⋮ — menu ide pod tlačidlo zarovnané doprava, kurzor — od kurzora. */
  zTlacidla?: boolean;
}

interface Props {
  poloha: PolohaMenu;
  onClose: () => void;
  /** „B2 · bok_L" — čoho sa menu týka (pri viacvýbere „3 dielce"). */
  hlavicka: string;
  zbalit: IkonaMenu & { rozbalene: boolean; label: string };
  kopirovat: IkonaMenu & { label: string };
  vlozit: IkonaMenu & { label: string };
  vystrihnut: IkonaMenu & { label: string };
  /** Posunúť riadok / zostavu hore a dole (KOLO 184, dovtedy v zozname). */
  hore: IkonaMenu & { label: string };
  dole: IkonaMenu & { label: string };
  children?: ReactNode;
}

function IkonaRadu({
  ikona: Ikona,
  label,
  skratka,
  akcia,
}: {
  ikona: LucideIcon;
  label: string;
  skratka?: string;
  akcia: IkonaMenu;
}): JSX.Element {
  const nedostupna = Boolean(akcia.dovod);
  const title = nedostupna ? `${label} — ${akcia.dovod}` : skratka ? `${label} (${skratka})` : label;
  // Zakázaná položka Radixu nechytá myš — `title` je preto na obale
  return (
    <span title={title} className="flex-1">
      <DropdownMenuItem
        aria-label={label}
        disabled={nedostupna}
        onSelect={() => akcia.onClick()}
        className="h-7 w-full justify-center p-0"
      >
        <Ikona className="h-4 w-4" />
      </DropdownMenuItem>
    </span>
  );
}

export function MenuRiadku({
  poloha,
  onClose,
  hlavicka,
  zbalit,
  kopirovat,
  vlozit,
  vystrihnut,
  hore,
  dole,
  children,
}: Props): JSX.Element {
  // Zámok tabuľky (KOLO 231): vložiť, vystrihnúť a posúvať zákazku menia —
  // zbaliť a kopírovať nie
  const zamok = useZamokTabulky();
  const zamkni = (a: IkonaMenu): IkonaMenu => (zamok ? { ...a, dovod: zamok } : a);
  return (
    <DropdownMenu open onOpenChange={(o) => !o && onClose()}>
      <DropdownMenuTrigger asChild>
        <span
          aria-hidden
          style={{ position: "fixed", left: poloha.x, top: poloha.y, width: 1, height: 1 }}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={poloha.zTlacidla ? "end" : "start"}
        sideOffset={poloha.zTlacidla ? 4 : 2}
        // Fokus sa nevracia na neviditeľný bod — ostane v tabuľke
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="w-52 p-1"
        data-testid="menu-riadku"
      >
        <div role="group" aria-label="Rýchle akcie" className="flex gap-0.5 border-b pb-0.5">
          <IkonaRadu
            ikona={zbalit.rozbalene ? ChevronsDownUp : ChevronsUpDown}
            label={zbalit.label}
            akcia={zbalit}
          />
          <IkonaRadu ikona={Copy} label={kopirovat.label} skratka="Ctrl+C" akcia={kopirovat} />
          <IkonaRadu
            ikona={ClipboardPaste}
            label={vlozit.label}
            skratka="Ctrl+V"
            akcia={zamkni(vlozit)}
          />
          <IkonaRadu
            ikona={Scissors}
            label={vystrihnut.label}
            skratka="Ctrl+X"
            akcia={zamkni(vystrihnut)}
          />
          <IkonaRadu ikona={ArrowUp} label={hore.label} skratka="Alt+↑" akcia={zamkni(hore)} />
          <IkonaRadu
            ikona={ArrowDown}
            label={dole.label}
            skratka="Alt+↓"
            akcia={zamkni(dole)}
          />
        </div>
        <div className="truncate px-2 pb-0.5 pt-1 text-[11px] font-semibold text-muted-foreground">
          {hlavicka}
        </div>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Položka menu s ikonou a skratkou vpravo. */
export function PolozkaMenu({
  ikona: Ikona,
  children,
  skratka,
  onSelect,
  nebezpecna = false,
  disabled: vypnuta = false,
  dovod: dovodPolozky,
  citanie = false,
}: {
  ikona: LucideIcon | ((p: { className?: string }) => JSX.Element);
  children: ReactNode;
  skratka?: string;
  onSelect: () => void;
  nebezpecna?: boolean;
  disabled?: boolean;
  /** Prečo je sivá — v tooltipe, text položky ostane krátky (KOLO 196). */
  dovod?: string;
  /**
   * Akcia zákazku nemení (zbaliť, pozrieť, kopírovať, šablóna) — ostane
   * aj pri zámku tabuľky. Ostatné položky zámok zošedí (KOLO 231).
   */
  citanie?: boolean;
}): JSX.Element {
  const zamok = useZamokTabulky();
  const zamknuta = zamok != null && !citanie;
  const disabled = vypnuta || zamknuta;
  const dovod = zamknuta ? zamok : dovodPolozky;
  const polozka = (
    <DropdownMenuItem
      onSelect={onSelect}
      disabled={disabled}
      className={cn(
        "gap-2 py-1 text-[13px]",
        nebezpecna && "text-destructive focus:text-destructive",
      )}
    >
      <Ikona className="h-3.5 w-3.5 shrink-0" />
      <span className="flex-1 truncate">{children}</span>
      {skratka ? (
        <span className="pl-2 text-[11px] text-muted-foreground">{skratka}</span>
      ) : null}
    </DropdownMenuItem>
  );
  // Sivá položka nedostane udalosti myši — tooltip nesie obal
  return disabled && dovod ? <div title={dovod}>{polozka}</div> : polozka;
}

/** Poloha menu pri ⋮ — pravý dolný roh tlačidla. */
export function polohaZTlacidla(el: HTMLElement): PolohaMenu {
  const r = el.getBoundingClientRect();
  return { x: r.right, y: r.bottom, zTlacidla: true };
}

/** Oddeľovač v menu riadku — tenší ako predvolený (KOLO 184). */
export function OddelovacMenu(): JSX.Element {
  return <DropdownMenuSeparator className="my-0.5" />;
}
